import z from 'zod'

//
// 打設箇所の名寄せ（表記揺れの統合）を AI で行うための入出力スキーマとプロンプト。
//
// 帳票ごとに以下のような表記揺れが発生する:
//   ・打設番号プレフィックスの有無:  「基礎1工区」＝「1 基礎1工区」
//   ・省略:                          「造成工事重力式擁壁②左、④」＝「重力式擁壁②左、④」
// これらを「同じ打設箇所」としてまとめ、各打設箇所名に正規化名(canonicalName)を与える。
//
// LLM に documentId を対応付けさせると誤りやすいため、入力は「打設箇所の文字列（原文）の集合」とし、
// 出力は「原文(originalName) → 正規化名(canonicalName)」の組とする。
//

// クラスタリング結果。OpenAI Structured Outputs(strict) 対応のため、
// 全プロパティ必須・追加プロパティ不可な単純構造にしている。
export const placementPlaceNameMatchResultSchema = z.object({
  results: z.array(
    z.object({
      // 入力で与えた打設箇所の原文。入力の文字列をそのまま返す。
      originalName: z.string(),
      // この原文に対応する正規化打設箇所名。同一箇所には同一の canonicalName を与える。
      canonicalName: z.string(),
    }),
  ),
})
export type PlacementPlaceNameMatchResult = z.infer<typeof placementPlaceNameMatchResultSchema>
export type PlacementPlaceNameMatchPair = PlacementPlaceNameMatchResult['results'][number]

//
// 名寄せプロンプトを組み立てる。
// anchors（人手確定済みの打設箇所名）は「正解ラベル」として必ず維持し、
// 表記揺れの原文はこのアンカーに寄せるよう指示する。再実行時の名前のブレ(flip-flop)を抑えるため、
// 代表名は「打設番号プレフィックスを除いた最も完全な表記」を選ぶよう明示する。
//
export function buildPlacementPlaceNameMatchPrompt(params: { originalNames: string[]; anchors: string[] }): string {
  const originalNamesJson = JSON.stringify(params.originalNames, null, 2)
  const anchorsJson = JSON.stringify(params.anchors, null, 2)

  return `# 役割
あなたは建設現場の生コン帳票に書かれた「打設箇所」の表記揺れを名寄せ（正規化）する専門エージェントです。

# 前提
- 同じ打設箇所が、帳票ごとに異なる表記で書かれていることがあります。代表的な揺れ:
  - 打設番号プレフィックスの有無: 「基礎1工区」と「1 基礎1工区」は同一。
  - 省略: 「造成工事重力式擁壁②左、④」と「重力式擁壁②左、④」は同一。
  - 全角/半角・空白の差: 「基礎2工区」と「基礎２工区」は同一。

# タスク
- 入力で与えられた打設箇所の原文(originalName)それぞれに対し、正規化名(canonicalName)を1つ与えてください。
- 「同じ打設箇所」と判断した原文どうしには、必ず同一の canonicalName を与えてください。
- canonicalName は「打設番号プレフィックスを除いた、最も完全で情報量の多い表記」を選んでください
  （例: 「1 基礎1工区」より「基礎1工区」、省略形より完全形を優先）。

# 厳守事項
- 入力のすべての originalName をちょうど1回ずつ出力に含めてください（過不足なく）。originalName は入力の文字列をそのまま返してください。
- 次の「確定済みの打設箇所名(anchors)」は人手で確定された正解です。表記揺れの原文はこれらに寄せ、該当する canonicalName は必ずこのアンカーの文字列と完全一致させてください。
- 明確に別物だと判断できる打設箇所は、無理に統合せず別々の canonicalName にしてください。
- 番号や左右・方角（①②、左右、東西南北 等）が異なるものは別の打設箇所です。統合しないでください。

# 確定済みの打設箇所名(anchors)
${anchorsJson}

# 入力（打設箇所の原文一覧）
${originalNamesJson}`
}
